import { getLuxLoyaltyAccount } from '~/services/LuxLoyalty';
import UsersService from '~/services/UsersService';

export type InfiniteMemberRole = 'PRIMARY' | 'SECONDARY';

export const INFINITE_MEMBER_ROLE_LABELS: Record<InfiniteMemberRole, string> = {
  PRIMARY: 'Primary',
  SECONDARY: 'Secondary',
};

export type InfiniteUserContact = {
  fullName: string;
  email: string;
  phone: string;
  phonePrefix: string;
  role: InfiniteMemberRole | null;
  enrolledAt: string | null;
};

export function isInfiniteMemberRole(role: string | null | undefined): role is InfiniteMemberRole {
  return role === 'PRIMARY' || role === 'SECONDARY';
}

/** Reads the Infinite profile captured when the member was enrolled. */
export function readInfiniteMemberDetails(account: App.LuxLoyaltyAccount | null): InfiniteUserContact | null {
  const profile = account?.infiniteProfile;
  if (!profile) {
    return null;
  }

  return {
    fullName: [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim(),
    email: profile.email?.trim() ?? '',
    phone: profile.phone?.trim() ?? '',
    phonePrefix: profile.phonePrefix?.trim() ?? '',
    role: isInfiniteMemberRole(profile.role) ? profile.role : null,
    enrolledAt: profile.createdAt ?? null,
  };
}

const CONTACT_FETCH_CONCURRENCY = 8;

function readContactString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function readContact(user: {
  fullName?: string;
  email?: string;
  phone?: unknown;
  phone_prefix?: unknown;
}): InfiniteUserContact {
  return {
    fullName: user.fullName?.trim() ?? '',
    email: user.email?.trim() ?? '',
    phone: readContactString(user.phone),
    phonePrefix: readContactString(user.phone_prefix),
    role: null,
    enrolledAt: null,
  };
}

/** Loads name, email, and phone for Infinite members. One missing user does not fail the rest. */
export async function loadInfiniteUserContacts(customerIds: Array<string>): Promise<Map<string, InfiniteUserContact>> {
  const contacts = new Map<string, InfiniteUserContact>();
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < customerIds.length) {
      const customerId = customerIds[nextIndex];
      nextIndex += 1;
      if (!customerId) {
        continue;
      }

      try {
        const account = await getLuxLoyaltyAccount(customerId);
        const member = readInfiniteMemberDetails(account);
        if (member) {
          contacts.set(customerId, member);
          continue;
        }
      } catch {
        // Fall through to the auth profile when the loyalty account cannot be read.
      }

      try {
        const user = await UsersService.getUser(customerId);
        contacts.set(customerId, readContact(user));
      } catch {
        // The membership row still renders from the loyalty account list if lookup fails.
      }
    }
  }

  const workerCount = Math.min(CONTACT_FETCH_CONCURRENCY, customerIds.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return contacts;
}
